/**
 * Onde cada descoberta mora — a regra, num lugar só.
 *
 * ═══ POR QUE ISTO VIROU UM MÓDULO ═══
 *
 * A regra estava escrita três vezes, em três arquivos, sempre como
 * `tom === 'acao' || tom === 'aviso'`. E ela era a causa de a tela de
 * Inteligência ficar vazia: medido em 04/09/2026, com 9.682 objetos no
 * banco, ela mostrava UMA frase em 7 dias.
 *
 * O motivo é que TOM e ENDEREÇO eram a mesma coisa. Um achado grave —
 * "62% do que essa câmera vê é descartado" — precisa de tom forte para
 * parecer grave. Só que tom forte significava "isto é manutenção", e a
 * Inteligência removia manutenção. Tudo que o software descobria
 * sozinho, ele mesmo jogava fora.
 *
 * Agora o servidor manda `secao`, e o tom volta a ser só aparência.
 */

import type { Descoberta, SecaoDaDescoberta } from './api/tipos'

/**
 * As seções da Inteligência, na ordem em que a tela as mostra.
 *
 * A ordem é informação, e não arrumação: a confiança vem primeiro
 * porque um número em que não se pode confiar não deve ser lido antes
 * do aviso de que ele é duvidoso.
 */
export const SECOES: {
  chave: Exclude<SecaoDaDescoberta, 'manutencao'>
  titulo: string
  explica: string
  /** O rodapé é sobre a captação, não sobre o negócio. Fica menor. */
  discreta?: boolean
}[] = [
  {
    chave: 'destaque',
    titulo: 'O que chama atenção',
    explica: 'O que mais mudou ou se destaca no período.',
  },
  {
    chave: 'ritmo',
    titulo: 'O ritmo daqui',
    explica: 'Quando enche, quando esvazia, quanto tempo as pessoas ficam.',
  },
  {
    chave: 'mudanca',
    titulo: 'Seus indicadores',
    explica: 'O que mudou no que você escolheu acompanhar.',
  },
  {
    /* ⚠️ POR ÚLTIMO, E DISCRETA — a primeira versão desta tela abria com
       "Em que não dá para confiar", e ninguém abre um painel para saber
       em que não confiar. A qualidade da captação importa, mas é rodapé:
       quem lê precisa dela depois de ver os números, não antes. */
    chave: 'captacao',
    titulo: 'Sobre a captação',
    explica: 'O que pode estar afetando os números acima.',
    discreta: true,
  },
]

/**
 * A seção da descoberta, com o padrão para respostas antigas.
 *
 * Um servidor que ainda não subiu não manda `secao`. Cair em 'mudanca'
 * mantém a tela cheia nesse intervalo, em vez de vazia — o oposto do
 * defeito que este módulo existe para consertar.
 */
export function secaoDe(d: Descoberta): SecaoDaDescoberta {
  return d.secao ?? 'mudanca'
}

/** As seções que têm alguma descoberta, na ordem da tela. */
export function secoesComConteudo(itens: Descoberta[]) {
  return SECOES.map(s => ({ ...s, itens: daSecao(itens, s.chave) }))
                .filter(s => s.itens.length > 0)
}

/** Precisa de providência: é o que a tela de Avisos mostra. */
export function ehManutencao(d: Descoberta): boolean {
  return secaoDe(d) === 'manutencao'
}

/** É análise: é o que a tela de Inteligência mostra. */
export function ehAnalise(d: Descoberta): boolean {
  return !ehManutencao(d)
}

/** As descobertas de uma seção, já ordenadas por peso. */
export function daSecao(itens: Descoberta[],
                        secao: SecaoDaDescoberta): Descoberta[] {
  return itens.filter(d => secaoDe(d) === secao)
              .sort((a, b) => b.peso - a.peso)
}
